// Need to use the React-specific entry point to import createApi
/* eslint-disable no-param-reassign */
import { createApi } from '@reduxjs/toolkit/query/react';
import { Memberships } from '@shared/types';
import { transformResponse } from '@utilities/api';
import fetchBaseQuery from '@utilities/fetchBaseQuery';

// Define a service using a base URL and expected endpoints
export const membershipApi = createApi({
  reducerPath: 'membershipApi',
  tagTypes: ['Membership'],
  baseQuery: fetchBaseQuery,
  endpoints: (builder) => ({
    /**
     * Get memberships
     */
    getMemberships: builder.query<Memberships[], null>({
      query: () => `user/me/memberships`,
      transformResponse,
      providesTags: ['Membership'],
      // providesTags: (result) =>
      //   result
      //     ? result.map(({ uid }) => ({ uid, type: 'Membership' }))
      //     : ['Membership'],
    }),

    /**
     * Check if user can update license plate
     */
    canUpdateLicensePlate: builder.query({
      query: (uid) => `/membership/${uid}/update-licenseplate`,
      transformResponse: (response) => ({
        isChangeable: response?.response?.changeAble ?? null,
        changeableDate: response?.response?.changeable_date ?? null,
      }),
      providesTags: (result) => [{ type: 'Membership', id: result?.uid }],
    }),

    /**
     * Get termination information (Such as end date etc.)
     */
    getTerminationInformation: builder.query({
      query: (uid) => `/membership/${uid}/termination-information`,
      transformResponse,
    }),

    /**
     * Check if user can update location
     */
    canUpdateLocation: builder.query({
      query: (uid) => `/membership/${uid}/update-location`,
      transformResponse: (response) => ({
        isChangeable: response?.response?.changeAble ?? null,
        changeableDate: response?.response?.changeable_date ?? null,
        last_changed: response?.response?.last_change ?? null,
      }),
      providesTags: (result) => [{ type: 'Membership', id: result?.uid }],
    }),

    /**
     * Cancel downgrade
     */
    cancelDownGrade: builder.mutation({
      query: (uid) => ({
        url: `/membership/${uid}/cancel-downgrade`,
        method: 'GET',
      }),
      transformResponse,
      invalidatesTags: ['Membership'],
    }),

    /**
     * Update membership
     */

    updateLicensePlate: builder.mutation({
      query: ({ uid, ...body }) => ({
        url: `/membership/${uid}/update-licenseplate`,
        method: 'PUT',
        body,
      }),
      transformResponse,
      invalidatesTags: ['Membership'],
    }),

    /**
     * Change addon
     */

    changeAddon: builder.mutation({
      query: ({ uid, ...body }) => ({
        url: `/membership/${uid}/change-addon`,
        method: 'PUT',
        body,
      }),
      transformResponse,
      invalidatesTags: ['Membership'],
    }),

    /**
     * Update location
     */

    updateLocation: builder.mutation({
      query: ({ uid, ...body }) => ({
        url: `/membership/${uid}/update-location`,
        method: 'PUT',
        body,
      }),
      transformResponse,
      invalidatesTags: ['Membership'],
    }),

    /**
     * Cancel downgrade ATAV
     */
    cancelATAVDownGrade: builder.mutation({
      query: (uid) => ({
        url: `/membership/${uid}/cancel-addon-expiration`,
        method: 'PUT',
        body: { plan: 'Plans5c2dc57944d15' },
      }),
      transformResponse,
      invalidatesTags: ['Membership'],
    }),

    /**
     * Cancel pause
     */
    cancelPause: builder.mutation({
      query: ({ uid, ...body }) => ({
        url: `/membership/${uid}/pause`,
        method: 'PUT',
        body,
      }),
      transformResponse,
      invalidatesTags: ['Membership'],
    }),

    /**
     * End membership
     */

    cancelMembership: builder.mutation({
      query: ({ convertToSingle, cancelReason, uid }) => ({
        url: `/membership/${uid}/delete`,
        method: 'PUT',
        body: {
          convertToSingle,
          cancelReason,
        },
      }),
      transformResponse,
      invalidatesTags: ['Membership'],
    }),

    /**
     * Cancel end membership
     */
    cancelExpiration: builder.mutation({
      query: (uid) => ({
        url: `/membership/${uid}/cancel-expiration`,
        method: 'GET',
      }),
      transformResponse,
      invalidatesTags: ['Membership'],
    }),

    /**
     * Change plan
     */

    changePlan: builder.mutation({
      query: ({ uid, ...body }) => ({
        url: `/user/me/memberships/${uid}/changeplan`,
        method: 'PUT',
        body,
      }),
      transformResponse,
      invalidatesTags: ['Membership'],
    }),

    /**
     * Set Pause
     */

    setPause: builder.mutation({
      query: ({ uid, ...body }) => ({
        url: `/membership/${uid}/pause`,
        method: 'POST',
        body,
      }),
      transformResponse,
      invalidatesTags: ['Membership'],
    }),

    countMemberships: builder.mutation({
      query: ({ wallet, user, hash, old_plan }) => ({
        url: `memberships/count`,
        method: 'POST',
        body: {
          wallet,
          user,
          hash,
          old_plan,
        },
      }),
      transformResponse,
      invalidatesTags: ['Membership'],
    }),

    upgradeMemberships: builder.mutation({
      query: ({ wallet, user, hash, old_plan, new_plan }) => ({
        url: `memberships/fast-upgrade`,
        method: 'POST',
        body: {
          wallet,
          user,
          hash,
          old_plan,
          new_plan,
        },
      }),
      transformResponse,
      invalidatesTags: ['Membership'],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetMembershipsQuery,

  useCanUpdateLicensePlateQuery,
  useGetTerminationInformationQuery,
  useCanUpdateLocationQuery,

  useUpdateLocationMutation,

  useUpdateLicensePlateMutation,
  useCancelDownGradeMutation,
  useCancelATAVDownGradeMutation,

  useChangeAddonMutation,

  useCancelMembershipMutation,
  useCancelExpirationMutation,
  useCancelPauseMutation,

  useChangePlanMutation,
  useSetPauseMutation,

  useCountMembershipsMutation,
  useUpgradeMembershipsMutation,
} = membershipApi;
